import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X, Trash2, Archive, FileX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * DeleteMemberModal - Modal per cancellazione membro con scelta soft/hard delete
 */
const DeleteMemberModal = ({
    isOpen,
    onClose,
    onConfirm,
    memberName,
    memberEmail,
    loading = false
}) => {
    const { t } = useTranslation();
    const [deleteMode, setDeleteMode] = useState('soft'); // 'soft' o 'hard'
    const [confirmText, setConfirmText] = useState('');
    const [isConfirmed, setIsConfirmed] = useState(false);

    // Reset campi quando si apre il modal o cambia il membro
    useEffect(() => {
        if (isOpen) {
            setConfirmText('');
            setIsConfirmed(false);
            setDeleteMode('soft');
        }
    }, [isOpen, memberName]);

    const handleConfirmChange = (e) => {
        const text = e.target.value;
        setConfirmText(text);
        // WEBAPP-007: Cambiato da memberName a "cancella"
        setIsConfirmed(text.trim().toLowerCase() === 'cancella');
    };

    const handleConfirm = async () => {
        if (!isConfirmed || loading) return;
        await onConfirm(deleteMode);
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
                    />

                    {/* Modal */}
                    <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.9, opacity: 0, y: 20 }}
                            transition={{ type: "spring", duration: 0.5 }}
                            className="relative flex max-h-[90vh] max-w-lg w-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Close button */}
                            <button
                                onClick={onClose}
                                className="absolute top-3 right-3 z-10 rounded-lg p-2 transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                            >
                                <X size={20} className="text-gray-500 dark:text-white/80" />
                            </button>

                            <div className="flex min-h-14 shrink-0 items-center border-b border-gray-200 bg-[#dad8e5] px-5 pr-14 dark:border-white/10 dark:bg-gray-800">
                                <h3 className="text-lg font-bold text-gray-900 dark:font-open-sans dark:text-white">
                                    {t('gym.membersManager.deleteClientTitle')}
                                </h3>
                            </div>

                            <div className="min-h-0 flex-1 overflow-y-auto bg-white px-6 py-6 dark:bg-gray-900">
                            {/* Icon */}
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.2, type: "spring" }}
                                className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30"
                            >
                                <Trash2 className="text-red-600" size={32} />
                            </motion.div>

                            {/* Member Info */}
                            <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-gray-900">
                                <p className="font-medium text-gray-900 dark:text-white mb-1">
                                    {memberName}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                    {memberEmail}
                                </p>
                            </div>

                            {/* Mode Selection */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                    Scegli modalità di cancellazione:
                                </label>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {/* Soft Delete Option */}
                                    <div
                                        onClick={() => setDeleteMode('soft')}
                                        className={`
                                            p-4 rounded-xl border-2 cursor-pointer transition-all h-full
                                            ${deleteMode === 'soft'
                                                ? 'border-blue-500 bg-blue-50 dark:border-indigo-500 dark:bg-gray-950'
                                                : 'border-gray-200 dark:border-white/15 hover:border-gray-300 dark:hover:border-white/25'
                                            }
                                        `}
                                    >
                                        <div className="flex items-start gap-3">
                                            <input
                                                type="radio"
                                                checked={deleteMode === 'soft'}
                                                onChange={() => setDeleteMode('soft')}
                                                className="mt-1"
                                            />
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Archive size={18} className="text-blue-600" />
                                                    <span className="font-medium text-gray-900 dark:text-white">
                                                        Mantieni Storico
                                                    </span>
                                                </div>
                                                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                                                    Elimina account e accesso, ma mantiene:
                                                </p>
                                                <ul className="text-xs text-gray-500 dark:text-gray-400 space-y-1 ml-6">
                                                    <li>• Storico allenamenti</li>
                                                    <li>• Progressi e misure</li>
                                                    <li>• Storico iscrizioni corsi</li>
                                                    <li>• Statistiche palestra</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Hard Delete Option */}
                                    <div
                                        onClick={() => setDeleteMode('hard')}
                                        className={`
                                            p-4 rounded-xl border-2 cursor-pointer transition-all h-full
                                            ${deleteMode === 'hard'
                                                ? 'border-red-500 bg-red-50 dark:border-red-400/50 dark:bg-red-950/30'
                                                : 'border-gray-200 dark:border-white/15 hover:border-gray-300 dark:hover:border-white/25'
                                            }
                                        `}
                                    >
                                        <div className="flex items-start gap-3">
                                            <input
                                                type="radio"
                                                checked={deleteMode === 'hard'}
                                                onChange={() => setDeleteMode('hard')}
                                                className="mt-1"
                                            />
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <FileX size={18} className="text-red-600" />
                                                    <span className="font-medium text-gray-900 dark:text-white">
                                                        Cancellazione Completa
                                                    </span>
                                                </div>
                                                <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                                                    Elimina TUTTI i dati del membro:
                                                </p>
                                                <ul className="text-xs text-gray-500 dark:text-gray-400 space-y-1 ml-6">
                                                    <li>• Account e accesso</li>
                                                    <li>• Storico allenamenti</li>
                                                    <li>• Progressi e misure</li>
                                                    <li>• Iscrizioni corsi</li>
                                                    <li>• Tutti i dati correlati</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Warning */}
                            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-6">
                                <div className="flex items-start gap-2">
                                    <AlertTriangle size={18} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                        {deleteMode === 'hard'
                                            ? 'Questa operazione è irreversibile e cancellerà definitivamente tutti i dati del membro.'
                                            : 'L\'utente non potrà più accedere, ma i dati storici verranno mantenuti per le statistiche della palestra.'
                                        }
                                    </p>
                                </div>
                            </div>

                            {/* Confirmation Input - WEBAPP-007: Cambiato da nome a "cancella" */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Per confermare l'eliminazione, digita <span className="font-bold text-red-600">"cancella"</span>:
                                </label>
                                <input
                                    type="text"
                                    value={confirmText}
                                    onChange={handleConfirmChange}
                                    placeholder="cancella"
                                    className="
                                        w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900
                                        focus:outline-none focus:ring-2 focus:ring-red-500
                                        dark:border-white/20 dark:bg-gray-800 dark:text-white dark:focus:ring-indigo-500/30
                                    "
                                />
                                {confirmText && !isConfirmed && (
                                    <p className="mt-1 text-xs text-red-600">
                                        Devi digitare esattamente "cancella"
                                    </p>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="flex gap-3">
                                <button
                                    onClick={onClose}
                                    disabled={loading}
                                    className="
                                        flex-1 rounded-xl px-4 py-3 font-medium transition-colors
                                        bg-gray-100 text-gray-900 hover:bg-gray-200
                                        dark:bg-gray-800 dark:font-open-sans dark:text-white dark:hover:brightness-110
                                        disabled:cursor-not-allowed disabled:opacity-50
                                    "
                                >
                                    Annulla
                                </button>
                                <button
                                    onClick={handleConfirm}
                                    disabled={!isConfirmed || loading}
                                    className={`
                                        flex-1 px-4 py-3 rounded-xl font-medium text-white transition-all
                                        focus:outline-none focus:ring-4 focus:ring-opacity-50
                                        disabled:opacity-50 disabled:cursor-not-allowed
                                        ${deleteMode === 'hard'
                                            ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                                            : 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500'
                                        }
                                        ${loading ? 'cursor-wait' : ''}
                                    `}
                                >
                                    {loading ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Eliminazione...
                                        </span>
                                    ) : (
                                        `Elimina ${deleteMode === 'hard' ? 'Definitivamente' : 'cliente'}`
                                    )}
                                </button>
                            </div>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
};

export default DeleteMemberModal;

